import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { useAzureMaps } from "react-azure-maps";
import { IContainerPoint } from "../interfaces/IContainerPoint";
import { wrapperStyles } from "../styles/style";

interface ContainerSearchBoxProps {
    containerPoints: IContainerPoint[];
    onSelect?: (point: IContainerPoint) => void;
}

export const ContainerSearchBox: React.FC<ContainerSearchBoxProps> = ({ containerPoints, onSelect }) => {
    const { mapRef } = useAzureMaps();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<IContainerPoint[]>([]);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const skipNextEffect = useRef(false);

    useEffect(() => {
        if (skipNextEffect.current) {
            skipNextEffect.current = false;
            return;
        }
        if (query.length < 2) {
            setSuggestions([]);
            return;
        }
        const lower = query.toLowerCase();
        setSuggestions(
            containerPoints
                .filter(c => c.containerNumber != null && c.containerNumber.toLowerCase().includes(lower))
                .slice(0, 8)
        );
    }, [query, containerPoints]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setSuggestions([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (point: IContainerPoint) => {
        skipNextEffect.current = true;
        setQuery(point.containerNumber);
        setSuggestions([]);
        if (mapRef) {
            mapRef.setCamera({
                center: [point.longitude, point.latitude],
                zoom: 14,
                type: 'ease',
                duration: 500,
            });
        }
        if (onSelect) onSelect(point);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        const lower = query.toLowerCase();
        const exact = containerPoints.find(c => c.containerNumber != null && c.containerNumber.toLowerCase() === lower);
        const match = exact !== undefined ? exact : suggestions[0];
        if (match) handleSelect(match);
    };

    return (
        <div ref={wrapperRef} style={wrapperStyles.searchBoxWrapper}>
            <input
                type="text"
                placeholder="Szukaj kontenera..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                style={wrapperStyles.searchInput}
            />
            {suggestions.length > 0 && (
                <div style={wrapperStyles.searchSuggestions}>
                    {suggestions.map(s => (
                        <div
                            key={s.id}
                            style={wrapperStyles.searchSuggestionItem}
                            onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
                        >
                            {s.containerNumber}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
